"use client";
import React from "react";
import { Container, Typography, Box, Button } from "@mui/material";
import { styled, Stack } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import "../app/globals.css";
import { IMAGES } from "../constants/images";
import NextLink from "next/link";
// Styled components
const MainHeading = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 500,
  marginBottom: "30px",
  color: "#020617",
  fontFamily: "var(--font-plusjakarta)",
  "& span": {
    color: "#775CE5",
    fontFamily: "var(--font-plusjakarta)",
    fontWeight: 600,
  },
  [theme.breakpoints.down("lg")]: {
    textAlign: "left",
    fontSize: "1.5rem",
    marginBottom: "20px",
  },
  [theme.breakpoints.up("lg")]: {
    textAlign: "center",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "1.5rem",
    marginBottom: "18px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.2rem",
    marginBottom: "15px",
  },
}));

const ContentBox = styled(Typography)(({ theme }) => ({
  marginBottom: "30px",
  lineHeight: 1.6,
  fontSize: "1.1rem",
  color: "#02061799",
  fontFamily: "var(--font-outfit)",
  fontWeight: 400,
  [theme.breakpoints.down("lg")]: {
    textAlign: "left",
    fontSize: "0.95rem",
    marginBottom: "25px",
  },
  [theme.breakpoints.up("lg")]: {
    textAlign: "center",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "0.9rem",
    marginBottom: "20px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    marginBottom: "18px",
    textAlign: "left",
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "20px",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  marginTop: "40px",
  [theme.breakpoints.down("lg")]: {
    justifyContent: "flex-start",
    marginTop: "30px",
  },
  [theme.breakpoints.down("md")]: {
    marginTop: "25px",
    justifyContent: "center",
  },
  [theme.breakpoints.down("sm")]: {
    marginTop: "20px",
    alignItems: "center",
  },
}));

const GuaranteeComponent = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  // Use Arabic image when language is Arabic, otherwise use default
  const guaranteeImage = isArabic
    ? IMAGES.RISK_FREE_ARABIC_IMG
    : IMAGES.RISK_FREE_GUARANTEE;

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 3, sm: 4, md: 5, lg: 6 },
        position: "relative",
        px: { xs: 2, sm: 3, md: 3 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      {/* Top Image */}
      <Box
        sx={{
          display: "flex",
          justifyContent: {
            xs: "flex-start",
            sm: "center",
            md: "center",
            lg: "center",
          },
          mb: { xs: 2, sm: 2.5, md: 3, lg: 4 },
        }}
      >
        <Image
          src={guaranteeImage}
          alt="Guaranteed"
          width={500}
          height={200}
          style={{
            maxWidth: "100%",
            height: "auto",
          }}
        />
      </Box>

      {/* Main Heading */}
      <MainHeading variant="h2" component="h2">
        {t("guarantee.heading1")}
        <span>{t("guarantee.headingHighlight")}</span>
        {t("guarantee.heading2")}
      </MainHeading>

      {/* Content Sections */}
      <Box
        sx={{
          maxWidth: { xs: "100%", lg: "730px" },
          mx: { xs: 0, sm: 0, md: 0, lg: "auto" },
        }}
      >
        <ContentBox>{t("guarantee.desc")}</ContentBox>

        {/* Action Buttons */}
        <ButtonContainer>
          <Stack
            direction={{ xs: "column", sm: "row", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center", md: "center" }}
            sx={{
              mb: -1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
              <Button
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: "#775CE5",
                  color: "#fff",
                  textTransform: "none",
                  px: { xs: 2.5, sm: 2 },
                  height: { xs: "48px", sm: "50px", md: "54px", lg: "50px" },
                  minWidth: { xs: "100%", sm: "150px", md: "150px" },
                  fontFamily: "var(--font-outfit)",
                  fontSize: {
                    xs: "0.85rem",
                    sm: "0.9rem",
                    md: "0.95rem",
                    lg: "1rem",
                  },
                  fontWeight: "500",
                  borderRadius: "28px",
                  boxShadow: "none",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "none",
                    transform: "scale(1.05) translateY(-2px)",
                    bgcolor: "#000",
                  },
                }}
              >
                {t("guarantee.cta")}
              </Button>
            </NextLink>
          </Stack>
        </ButtonContainer>

        {/* Footer Text */}
        <Typography
          variant="body1"
          sx={{
            justifyContent: {
              xs: "flex-start",
              sm: "center",
              md: "center",
              lg: "center",
            },
            textAlign: {
              xs: isArabic ? "right" : "left",
              sm: "center",
              md: "center",
              lg: "center",
            },
            color: "#02061799",
            maxWidth: { xs: "100%", lg: "500px" },
            mx: { xs: 0, sm: 0, md: "auto", lg: "auto" },
            fontWeight: 500,
            fontFamily: "var(--font-plusjakarta)",
            mt: { xs: "6%", sm: "7%", md: "8%", lg: "5%" },
            fontSize: { xs: "13px", sm: "14px", md: "15px", lg: "16px" },
          }}
        >
          <span style={{ color: "#020617", fontWeight: "900" }}>
            {t("guarantee.psHighlight")}
          </span>{" "}
          {t("guarantee.ps")}
        </Typography>
      </Box>
    </Container>
  );
};

export default GuaranteeComponent;
